import { useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import { useOutletContext } from "react-router-dom";
import { authAtom } from "../../lib/auth-store.js";
import { apiCall } from "../../lib/api-client.js";
import { ApiError } from "../../lib/api-types.js";
import type {
  LlmProvider,
  LlmProviderConfig,
  LlmTestResult,
} from "../../lib/api-types.js";
import { llmConfigPath, llmConfigTestPath } from "../../lib/api-paths.js";
import { PageHeader } from "../../components/PageHeader.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import {
  FormField,
  PrimaryButton,
  SecondaryButton,
  Select,
  TextInput,
} from "../../components/FormField.js";
import { Spinner } from "../../components/Spinner.js";
import { formatDateTime } from "../../lib/format.js";
import { cn } from "../../lib/cn.js";
import type { OrgDetailContext } from "./context.js";

interface FormState {
  provider: LlmProvider;
  apiKey: string;
  model: string;
  baseUrl: string;
}

const PROVIDER_OPTIONS: ReadonlyArray<{ value: LlmProvider; label: string }> = [
  { value: "anthropic", label: "Anthropic (Claude)" },
  { value: "openai-compat", label: "OpenAI 兼容 (DeepSeek / Qwen / 自建)" },
];

const DEFAULT_MODELS: Record<LlmProvider, string> = {
  anthropic: "claude-opus-4-5",
  "openai-compat": "deepseek-chat",
};

export function LlmConfigPanel(): JSX.Element {
  const { org } = useOutletContext<OrgDetailContext>();
  const auth = useAtomValue(authAtom);
  const [config, setConfig] = useState<LlmProviderConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>({
    provider: "anthropic",
    apiKey: "",
    model: DEFAULT_MODELS.anthropic,
    baseUrl: "",
  });
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<LlmTestResult | null>(null);
  const [testing, setTesting] = useState<boolean>(false);

  useEffect(() => {
    if (!auth) return;
    (async () => {
      try {
        const data = await apiCall<LlmProviderConfig | null>(
          "GET",
          llmConfigPath(org.code),
          { token: auth.token },
        );
        setConfig(data);
        if (data) {
          setForm({
            provider: data.provider,
            apiKey: "",
            model: data.model,
            baseUrl: data.baseUrl ?? "",
          });
        }
      } catch (err) {
        const msg = err instanceof ApiError ? err.message : "加载失败";
        setLoadError(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, [auth, org.code]);

  const handleProviderChange = (next: LlmProvider): void => {
    setForm((f) => ({
      ...f,
      provider: next,
      model: config?.provider === next ? f.model : DEFAULT_MODELS[next],
      baseUrl: next === "anthropic" ? "" : f.baseUrl,
    }));
  };

  const handleSave = async (): Promise<void> => {
    if (!auth) return;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);
    try {
      const body: {
        provider: LlmProvider;
        apiKey?: string;
        model: string;
        baseUrl?: string;
      } = {
        provider: form.provider,
        model: form.model.trim(),
      };
      if (form.apiKey.trim()) body.apiKey = form.apiKey.trim();
      if (form.baseUrl.trim()) body.baseUrl = form.baseUrl.trim();
      const data = await apiCall<LlmProviderConfig>(
        "PUT",
        llmConfigPath(org.code),
        { token: auth.token, body },
      );
      setConfig(data);
      setForm((f) => ({ ...f, apiKey: "" }));
      setSubmitSuccess("已保存");
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "保存失败";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleTest = async (): Promise<void> => {
    if (!auth) return;
    setTesting(true);
    setTestResult(null);
    try {
      const body: {
        provider: LlmProvider;
        apiKey?: string;
        model: string;
        baseUrl?: string;
      } = {
        provider: form.provider,
        model: form.model.trim(),
      };
      if (form.apiKey.trim()) body.apiKey = form.apiKey.trim();
      if (form.baseUrl.trim()) body.baseUrl = form.baseUrl.trim();
      const data = await apiCall<LlmTestResult>(
        "POST",
        llmConfigTestPath(org.code),
        { token: auth.token, body },
      );
      setTestResult(data);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "测试失败";
      setTestResult({ ok: false, error: msg });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div>
        <PageHeader title="LLM 配置" />
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="LLM 配置"
        subtitle="设置组织角色推荐使用的大模型。支持 Anthropic（Claude）和 OpenAI 兼容协议（DeepSeek / Qwen / 自建）。"
      />

      {loadError && <ErrorBanner>{loadError}</ErrorBanner>}

      {config && (
        <div className="mb-3 text-xs text-dosia-text-secondary">
          当前：{config.provider} · {config.model}
          {config.configuredAt && (
            <> · 最近更新 {formatDateTime(config.configuredAt)}</>
          )}
          {config.configuredBy && <> · 由 {config.configuredBy} 配置</>}
        </div>
      )}

      <div className="space-y-3 max-w-xl">
        <FormField label="Provider" required>
          <Select
            value={form.provider}
            onChange={(e) => handleProviderChange(e.target.value as LlmProvider)}
          >
            {PROVIDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </FormField>

        <FormField
          label="API Key"
          required={!config?.hasKey}
          hint={
            config?.hasKey
              ? "已配置（留空则保留原 key，输入新值替换）"
              : "首次配置请填写"
          }
        >
          <TextInput
            type="password"
            value={form.apiKey}
            placeholder={config?.hasKey ? "已配置，留空保留" : "sk-..."}
            onChange={(e) => setForm((f) => ({ ...f, apiKey: e.target.value }))}
            autoComplete="off"
          />
        </FormField>

        <FormField label="Model" required hint="如 claude-opus-4-5 / deepseek-chat / gpt-4o">
          <TextInput
            value={form.model}
            onChange={(e) => setForm((f) => ({ ...f, model: e.target.value }))}
          />
        </FormField>

        {form.provider === "openai-compat" && (
          <FormField
            label="Base URL"
            required
            hint="OpenAI 兼容服务的 endpoint，如 https://api.deepseek.com/v1"
          >
            <TextInput
              value={form.baseUrl}
              onChange={(e) =>
                setForm((f) => ({ ...f, baseUrl: e.target.value }))
              }
              placeholder="https://..."
            />
          </FormField>
        )}

        {submitError && <ErrorBanner>{submitError}</ErrorBanner>}
        {submitSuccess && (
          <div className="rounded-md border border-emerald-400/40 bg-emerald-400/15 px-3 py-2 text-sm text-emerald-700">
            {submitSuccess}
          </div>
        )}
        {testResult && (
          <div
            className={cn(
              "rounded-md border px-3 py-2 text-sm",
              testResult.ok
                ? "border-emerald-400/40 bg-emerald-400/15 text-emerald-700"
                : "border-amber-500/40 bg-amber-500/15 text-amber-700",
            )}
          >
            {testResult.ok
              ? `测试连接成功 · ${testResult.latencyMs ?? "?"} ms`
              : `测试失败：${testResult.error ?? "unknown"}`}
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <PrimaryButton onClick={handleSave} disabled={submitting}>
            {submitting ? "保存中..." : "保存"}
          </PrimaryButton>
          <SecondaryButton onClick={handleTest} disabled={testing}>
            {testing ? "测试中..." : "测试连接"}
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
}
